import cn from "classnames"
import { useRef } from "react"

import { useGetScanHistoryStatistics } from "@/api/useScanHistory"
import { Col, Flex, Row } from "@/atoms"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useTranslation } from "@/hooks/useTranslation"
import { InformationPopover } from "@/molecules/InformationPopover"
import { StatisticWidget, Widget } from "@/molecules/Widget"
import { Legend } from "./components/Legend"
import { ScansStackedColumn } from "./components/ScansStackedColumn"
import { TopInitiatorWidget } from "./components/TopInitiatorWidget"
import { getStatusTranslationKey } from "./components/options"

import styles from "./HeaderScanHistoryTab.module.scss"

import type { ScansStackedColumnProps } from "./components/ScansStackedColumn.types"

export const HeaderScanHistoryTab = () => {
  const { response, isLoading } = useGetScanHistoryStatistics()
  const { data } = response || {}
  const {
    totalScans = 0,
    totalPassed = 0,
    totalScanned = 0,
    totalFailed = 0,
    totalSkipped = 0
  } = data?.scanCounts || {}
  const totalPassedAndScanned = totalScanned + totalPassed
  const getPercentage = (part: number, total: number) =>
    total > 0 ? Math.round((part / total) * 100) : 0
  const failedPercentage = getPercentage(totalFailed, totalScans)
  const skippedPercentage = getPercentage(totalSkipped, totalScans)
  const passedAndScannedPercentage = 100 - failedPercentage - skippedPercentage
  const { abbreviateNumber } = useNumberConvertor()
  const { t } = useTranslation("translation", {
    keyPrefix: "governance.scanHistory"
  })

  const gap = 12
  const statuses = ["passed", "warn_and_pass", "failed"] as const
  const colorsByType = useRef<ScansStackedColumnProps["colorsByType"]>()

  if (!colorsByType.current) {
    const colors = getComputedStyle(document.documentElement)

    colorsByType.current = {
      passed: colors.getPropertyValue("--polar-green-3-color"),
      warn_and_pass: colors.getPropertyValue("--volcano-3-color"),
      failed: colors.getPropertyValue("--dust-red-3-color")
    }
  }

  return (
    <Row className={cn(styles.row, "sticky-section-tab-template-ks")}>
      <Col span={16}>
        <Row gutter={[gap, gap]}>
          <Col span={24}>
            <Row gutter={gap}>
              <Col span={6}>
                <StatisticWidget
                  border
                  loading={isLoading}
                  title={
                    <Flex align="center" justify="space-between">
                      {t("totalScans")}
                      <InformationPopover.Popover
                        content={
                          <InformationPopover.CommonTemplate>
                            {t("totalScansTooltip")}
                          </InformationPopover.CommonTemplate>
                        }
                      />
                    </Flex>
                  }
                  value={abbreviateNumber(totalScans)}
                />
              </Col>
              <Col span={6}>
                <StatisticWidget
                  border
                  loading={isLoading}
                  title={t("status.passed_scanned")}
                  value={abbreviateNumber(totalPassedAndScanned)}
                  percentage={passedAndScannedPercentage}
                />
              </Col>
              <Col span={6}>
                <StatisticWidget
                  border
                  loading={isLoading}
                  title={t("status.failed")}
                  value={abbreviateNumber(totalFailed)}
                  percentage={failedPercentage}
                />
              </Col>
              <Col span={6}>
                <StatisticWidget
                  border
                  loading={isLoading}
                  title={t("status.warn_and_pass")}
                  value={abbreviateNumber(totalSkipped)}
                  percentage={skippedPercentage}
                />
              </Col>
            </Row>
          </Col>
          <Col span={24} className={styles.column}>
            <Widget
              capitalize={false}
              loading={isLoading}
              title={t("dailyBreakdown.title")}
              popover={{ content: t("dailyBreakdown.tooltip") }}
              aside={
                <Legend
                  items={statuses.map((status) => ({
                    color: colorsByType.current?.[status] || "",
                    label: t(getStatusTranslationKey(status))
                  }))}
                />
              }
            >
              <ScansStackedColumn colorsByType={colorsByType.current} t={t} />
            </Widget>
          </Col>
        </Row>
      </Col>
      <Col span={8}>
        <TopInitiatorWidget />
      </Col>
    </Row>
  )
}
